import React from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, List, ListItemButton, ListItemText, Typography, Divider, Stack } from "@mui/material";
import {
  Settings,
  Ticket,
  Sliders,
  Flag,
  FolderTree,
  FileSpreadsheet,
  Users,
  UserCheck,
  Shield,
  Clock,
  Calendar,
  Zap,
  Mail,
  FileText,
  MessageSquare,
  Phone,
  Globe,
  MessageCircle,
  Bot,
  Webhook,
  History,
} from "lucide-react";
import { HelpdeskPage, useHd } from "../HelpdeskContext";
import { GlassCard, PageHeader, T, TYPE } from "../../user/complaints/shared";
import GeneralSettings from "./GeneralSettings";
import { StatusesEditor, PrioritiesEditor, CategoriesEditor, CustomFieldsEditor, CannedEditor } from "./ListEditors";
import { TeamsEditor, AgentsEditor, RolesEditor } from "./PeopleSettings";
import { SlaEditor, BusinessHoursEditor } from "./SlaSettings";
import AutomationSettings from "./AutomationSettings";
import { MailboxesEditor, TemplatesEditor } from "./EmailSettings";
import ChatSettings from "./ChatSettings";
import AiSettings from "./AiSettings";
import PortalSettings from "./PortalSettings";
import WhatsAppSettings from "./WhatsAppSettings";
import { WebhooksEditor, AuditLog } from "./WebhooksSettings";

const SETTING_GROUPS = [
  {
    title: "TICKETS",
    items: [
      { key: "statuses", label: "Statuses", desc: "Control the stages a ticket moves through", icon: Sliders, comp: StatusesEditor, perm: "settings.manage" },
      { key: "priorities", label: "Priorities", desc: "Define ticket urgency levels", icon: Flag, comp: PrioritiesEditor, perm: "settings.manage" },
      { key: "categories", label: "Categories", desc: "Organize tickets by issue type", icon: FolderTree, comp: CategoriesEditor, perm: "settings.manage" },
      { key: "fields", label: "Custom fields", desc: "Add additional ticket information", icon: FileSpreadsheet, comp: CustomFieldsEditor, perm: "settings.manage" },
      { key: "general", label: "Ticket configuration", desc: "Global ticket rules, limits and defaults", icon: Ticket, comp: GeneralSettings, perm: "settings.manage" },
    ],
  },
  {
    title: "TEAM & ACCESS",
    items: [
      { key: "teams", label: "Teams", desc: "Organize your support team", icon: Users, comp: TeamsEditor, perm: "settings.manage" },
      { key: "agents", label: "Agents", desc: "Manage support agents", icon: UserCheck, comp: AgentsEditor, perm: "settings.manage" },
      { key: "roles", label: "Roles & permissions", desc: "Control what users can access", icon: Shield, comp: RolesEditor, perm: "settings.manage" },
    ],
  },
  {
    title: "SERVICE MANAGEMENT",
    items: [
      { key: "sla", label: "SLA policies", desc: "Define response and resolution targets", icon: Clock, comp: SlaEditor, perm: "sla.view" },
      { key: "hours", label: "Business hours", desc: "Set your team's working hours", icon: Calendar, comp: BusinessHoursEditor, perm: "sla.view" },
      { key: "automation", label: "Automation", desc: "Automate repetitive support actions", icon: Zap, comp: AutomationSettings, perm: "automation.view" },
    ],
  },
  {
    title: "COMMUNICATION",
    items: [
      { key: "email", label: "Email & mailboxes", desc: "Connect support email accounts", icon: Mail, comp: MailboxesEditor, perm: "settings.manage" },
      { key: "templates", label: "Email templates", desc: "Manage customer email notifications", icon: FileText, comp: TemplatesEditor, perm: "settings.manage" },
      { key: "chat", label: "Chat settings", desc: "Configure live chat widget and behaviour", icon: MessageSquare, comp: ChatSettings, perm: "settings.manage" },
      { key: "whatsapp", label: "WhatsApp", desc: "Configure WhatsApp support channel", icon: Phone, comp: WhatsAppSettings, perm: "settings.manage" },
    ],
  },
  {
    title: "CUSTOMER EXPERIENCE",
    items: [
      { key: "portal", label: "Customer portal", desc: "Configure the customer-facing support experience", icon: Globe, comp: PortalSettings, perm: "settings.manage" },
      { key: "canned", label: "Canned responses", desc: "Create reusable agent replies", icon: MessageCircle, comp: CannedEditor, perm: "tickets.reply" },
    ],
  },
  {
    title: "AI & INTEGRATIONS",
    items: [
      { key: "ai", label: "AI settings", desc: "Configure AI support features", icon: Bot, comp: AiSettings, perm: "ai.configure" },
      { key: "webhooks", label: "Webhooks", desc: "Connect external services", icon: Webhook, comp: WebhooksEditor, perm: "settings.manage" },
      { key: "audit", label: "Audit log", desc: "Review important system activity", icon: History, comp: AuditLog, perm: "settings.manage" },
    ],
  },
];

function SettingsInner() {
  const ctx = useHd();
  const [params, setParams] = useSearchParams();

  // Filter groups according to permissions
  const filteredGroups = SETTING_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => ctx.can(item.perm)),
  })).filter((g) => g.items.length > 0);

  const allVisible = filteredGroups.flatMap((g) => g.items);
  const selectedKey = params.get("s") || allVisible[0]?.key || "statuses";
  const current = allVisible.find((item) => item.key === selectedKey) || allVisible[0];

  if (!current) return <Alert severity="warning">You do not have access to helpdesk settings.</Alert>;
  const Component = current.comp;
  const CurrentIcon = current.icon || Settings;

  return (
    <Box>
      <PageHeader
        icon={Settings}
        title="Support Settings"
        subtitle="Manage tickets, teams, SLAs, channels, automation, and AI configuration for your workspace"
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px minmax(0,1fr)" }, gap: 2.5, alignItems: "start" }}>
        {/* Settings Navigation Sidebar */}
        <GlassCard
          sx={{
            p: 1.5,
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
          }}
        >
          <List dense disablePadding>
            {filteredGroups.map((g, gIdx) => (
              <Box key={g.title} sx={{ mb: gIdx === filteredGroups.length - 1 ? 0 : 2 }}>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    color: "#98A2B3",
                    px: 1.5,
                    py: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {g.title}
                </Typography>
                {g.items.map((item) => {
                  const isSelected = item.key === current.key;
                  const Icon = item.icon;
                  return (
                    <ListItemButton
                      key={item.key}
                      selected={isSelected}
                      onClick={() => setParams({ s: item.key })}
                      sx={{
                        borderRadius: "8px",
                        my: 0.25,
                        px: 1.5,
                        py: 0.75,
                        "&.Mui-selected": {
                          bgcolor: "rgba(99, 102, 241, 0.12)",
                          color: "rgb(99, 102, 241)",
                          "& .MuiListItemIcon-root": { color: "rgb(99, 102, 241)" },
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, width: "100%" }}>
                        {Icon && <Icon size={16} color={isSelected ? "rgb(99, 102, 241)" : "#667085"} style={{ flexShrink: 0 }} />}
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: TYPE.small,
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? "rgb(99, 102, 241)" : "#344054",
                            noWrap: true,
                          }}
                        />
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </Box>
            ))}
          </List>
        </GlassCard>

        {/* Settings Content Area */}
        <GlassCard
          sx={{
            p: { xs: 2, sm: 3 },
            minWidth: 0,
            maxWidth: "100%",
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
          }}
        >
          <Box sx={{ pb: 2.5, mb: 3, borderBottom: `1px solid ${T.border}` }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  bgcolor: "rgba(99, 102, 241, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgb(99, 102, 241)",
                }}
              >
                <CurrentIcon size={20} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: T.ink }}>
                  {current.label}
                </Typography>
                <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.25 }}>
                  {current.desc}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Component />
        </GlassCard>
      </Box>
    </Box>
  );
}

export default function SupportSettings() {
  return (
    <HelpdeskPage>
      <SettingsInner />
    </HelpdeskPage>
  );
}
